import { useEffect, useMemo, useState } from 'react';

export const PAGE_SIZE_OPTIONS = [5, 10, 25];

export function clampPageSize(value, fallback = 10) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.max(1, Math.floor(n));
}

export function normalizeOffset({ offset = 0, total = 0, pageSize = 10 } = {}) {
  const size = clampPageSize(pageSize);
  const count = Math.max(0, Number(total) || 0);
  const raw = Math.max(0, Math.floor(Number(offset) || 0));
  if (count === 0) return 0;
  const lastOffset = Math.floor((count - 1) / size) * size;
  return Math.min(raw - (raw % size), lastOffset);
}

export function pageWindow({ offset = 0, total = 0, limit = 10 } = {}) {
  const pageSize = clampPageSize(limit);
  const count = Math.max(0, Number(total) || 0);
  const normalizedOffset = normalizeOffset({ offset, total: count, pageSize });
  const from = count === 0 ? 0 : normalizedOffset + 1;
  const to = count === 0 ? 0 : Math.min(count, normalizedOffset + pageSize);
  const pageCount = count === 0 ? 0 : Math.ceil(count / pageSize);
  const pageIndex = count === 0 ? 0 : Math.floor(normalizedOffset / pageSize) + 1;
  return {
    from,
    to,
    total: count,
    limit: pageSize,
    offset: normalizedOffset,
    pageIndex,
    pageCount,
    hasPrevious: normalizedOffset > 0,
    hasNext: normalizedOffset + pageSize < count,
    previousOffset: Math.max(0, normalizedOffset - pageSize),
    nextOffset: Math.min(Math.max(0, count - 1), normalizedOffset + pageSize),
  };
}

export function useClientPagination(items = [], { initialPageSize = 10, resetKey = '' } = {}) {
  const [pageSize, setPageSizeState] = useState(() => clampPageSize(initialPageSize));
  const [offset, setOffsetState] = useState(0);
  const total = items.length;

  useEffect(() => {
    setOffsetState(0);
  }, [resetKey, pageSize]);

  useEffect(() => {
    setOffsetState((current) => normalizeOffset({ offset: current, total, pageSize }));
  }, [total, pageSize]);

  const page = useMemo(
    () => pageWindow({ offset, total, limit: pageSize }),
    [offset, total, pageSize],
  );
  const pageItems = useMemo(
    () => items.slice(page.offset, page.offset + page.limit),
    [items, page.offset, page.limit],
  );

  return {
    items: pageItems,
    page,
    pageSize,
    setOffset: (nextOffset) =>
      setOffsetState(normalizeOffset({ offset: nextOffset, total, pageSize })),
    setPageSize: (nextPageSize) => {
      setPageSizeState(clampPageSize(nextPageSize, pageSize));
      setOffsetState(0);
    },
  };
}

export function readPagingParams(
  searchParams,
  { defaultPageSize = 10, pageSizeParam = 'pageSize', offsetParam = 'offset' } = {},
) {
  const pageSize = clampPageSize(searchParams.get(pageSizeParam), defaultPageSize);
  const offset = normalizeOffset({
    offset: searchParams.get(offsetParam),
    total: Number.MAX_SAFE_INTEGER,
    pageSize,
  });
  return { pageSize, offset };
}

export function writePagingParams(
  searchParams,
  {
    defaultPageSize = 10,
    pageSize,
    offset,
    pageSizeParam = 'pageSize',
    offsetParam = 'offset',
  } = {},
) {
  const next = new URLSearchParams(searchParams);
  const size = clampPageSize(pageSize, defaultPageSize);
  const normalizedOffset = normalizeOffset({
    offset,
    total: Number.MAX_SAFE_INTEGER,
    pageSize: size,
  });
  if (size === defaultPageSize) next.delete(pageSizeParam);
  else next.set(pageSizeParam, String(size));
  if (normalizedOffset === 0) next.delete(offsetParam);
  else next.set(offsetParam, String(normalizedOffset));
  return next;
}

export function useUrlPagination(searchParams, setSearchParams, options = {}) {
  const { defaultPageSize = 10, pageSizeParam = 'pageSize', offsetParam = 'offset' } = options;
  const { pageSize, offset } = readPagingParams(searchParams, {
    defaultPageSize,
    pageSizeParam,
    offsetParam,
  });
  const write = ({ nextPageSize = pageSize, nextOffset = offset } = {}) => {
    setSearchParams(
      writePagingParams(searchParams, {
        defaultPageSize,
        pageSize: nextPageSize,
        offset: nextOffset,
        pageSizeParam,
        offsetParam,
      }),
      { replace: true },
    );
  };
  return {
    pageSize,
    offset,
    setOffset: (nextOffset) => write({ nextOffset }),
    setPageSize: (nextPageSize) => write({ nextPageSize, nextOffset: 0 }),
  };
}
